import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Spin,
  Table,
  Skeleton,
  Grid
} from 'antd';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined
} from '@ant-design/icons';
import leadsService from '../../services/leads';

const {  Text } = Typography;
const { useBreakpoint } = Grid;

const DashboardPage = () => {
  const { currentUser } = useSelector((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';
  const [timeRange, setTimeRange] = React.useState('week');
  const screens = useBreakpoint();

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['leadStats', timeRange],
    queryFn: () => leadsService.getLeadStats(timeRange)
  });

  const { data: latestLeads, isLoading: leadsLoading, error: leadsError } = useQuery({
    queryKey: ['latestLeads'],
    queryFn: () => leadsService.getLatestLeads()
  });

  if (statsError || leadsError) {
    return <Text type="danger">Error loading dashboard: {statsError?.message || leadsError?.message}</Text>;
  }

  const leadsByStateData = stats?.leads_by_state || [];


  return (
    <div style={{ 
      padding: screens.xs ? '12px' : '24px', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh'
    }}>
      <div className="page-header" style={{
        marginBottom: screens.xs ? '16px' : '32px',
        padding: screens.xs ? '16px' : '24px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
            transition: 'transform 0.3s ease'
          }}>
            <UserOutlined style={{ fontSize: '20px', color: '#fff' }} />
          </div>
          <div>
            <Typography.Title level={2} style={{
              margin: 0,
              color: '#1f1f1f',
              background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '24px'
            }}>
              Dashboard Overview
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Monitor your leads and business performance
            </Typography.Text>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
            }}
            bodyStyle={{ padding: 24 }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
              pointerEvents: 'none'
            }} />
            {statsLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
                <Text strong style={{ color: '#fff', fontSize: 18, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Total Leads</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <UserOutlined style={{ color: '#fff', fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 32, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{stats?.total_leads || 0}</span>
                </div>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{
              background: 'linear-gradient(135deg, #52c41a 0%, #237804 100%)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
            }}
            bodyStyle={{ padding: 24 }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
              pointerEvents: 'none'
            }} />
            {statsLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
                <Text strong style={{ color: '#fff', fontSize: 18, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Available Leads</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ClockCircleOutlined style={{ color: '#fff', fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 32, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{stats?.available_leads || 0}</span>
                </div>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{
              background: 'linear-gradient(135deg, #722ed1 0%, #391085 100%)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
            }}
            bodyStyle={{ padding: 24 }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
              pointerEvents: 'none'
            }} />
            {statsLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
                <Text strong style={{ color: '#fff', fontSize: 18, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Purchased Leads</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircleOutlined style={{ color: '#fff', fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 32, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{stats?.purchased_leads || 0}</span>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={24}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
            title={
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DollarOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={5} style={{ margin: 0 }}>Leads by State</Typography.Title>
              </div>
            }
            extra={<Text type="secondary">Top States</Text>}
          >
            <div style={{ height: 400 }}>
              {statsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={leadsByStateData.sort((a, b) => b.count - a.count).slice(0, 10)} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1890ff" />
                        <stop offset="100%" stopColor="#13c2c2" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="state" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      tick={{ fontSize: 12, fill: '#666' }}
                      axisLine={{ stroke: '#d9d9d9' }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Number of Leads', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle', fill: '#666' }
                      }}
                      tick={{ fontSize: 12, fill: '#666' }}
                      axisLine={{ stroke: '#d9d9d9' }}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value} leads`, 'Count']}
                      labelStyle={{ color: '#666', fontWeight: 'bold' }}
                      contentStyle={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        padding: '12px'
                      }}
                      cursor={{ fill: 'rgba(24, 144, 255, 0.1)' }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="url(#barGradient)"
                      radius={[8, 8, 0, 0]}
                      barSize={40}
                      animationDuration={1500}
                      animationBegin={0}
                    >
                      {leadsByStateData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`}
                          fill={`hsl(${200 + index * 20}, 70%, 50%)`}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
            title={
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ClockCircleOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={5} style={{ margin: 0 }}>Recent Lead Activity</Typography.Title>
              </div>
            }
          >
            <Table
              dataSource={latestLeads || []}
              columns={[
                {
                  title: 'First Name',
                  dataIndex: 'first_name',
                  key: 'first_name',
                  width: '15%',
                },
                {
                  title: 'Last Name',
                  dataIndex: 'last_name',
                  key: 'last_name',
                  width: '15%',
                },
                {
                  title: 'Birth Date',
                  dataIndex: 'birth_date',
                  key: 'birth_date',
                  width: '15%',
                  render: (value) => {
                    if (!value) return '-';
                    return new Date(value).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric'
                    });
                  }
                },
                {
                  title: 'Coverage Amount',
                  dataIndex: 'coverage_amount',
                  key: 'coverage_amount',
                  width: '15%',
                  render: (value) => value || '-'
                },
                {
                  title: 'State',
                  dataIndex: 'state_name',
                  key: 'state_name',
                  width: '15%',
                },
             
              ]}
              pagination={false}
              rowKey="id"
              size="middle"
              scroll={{ x: 800 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage; 